import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import ChatMessage from './ChatMessage';

// Distance from the bottom (px) under which we still consider the user
// "at the latest" — keeps auto-scroll from yanking on slight drift.
// Duplicated from ChatWidget so the thread is self-contained for reuse
// in non-widget contexts (e.g. the offer→bookings ingest page).
const NEAR_BOTTOM_THRESHOLD = 80;

/**
 * Scrollable chat-thread renderer. Owns its own scroll tracking + the
 * "scroll to latest" floater so it works identically inside the
 * floating ChatWidget and the full-page IngestWorkflow.
 *
 * Imperative handle exposes `scrollToLatest(behavior)` so the parent
 * can force-scroll after the operator sends a message (otherwise the
 * "near-bottom" heuristic would leave them stuck at a prior message
 * when their own input appears at the bottom of the new turn).
 */
const ChatThread = forwardRef((
  {
    blocks,
    pending,
    error,
    status,
    onAnswer,
    onDecideApproval,
    placeholder,
    className,
  },
  ref,
) => {
  const scrollRef = useRef(null);
  // Track whether the user is currently at (or near) the latest message.
  // Stored in a ref so the auto-scroll effect can read it without re-
  // firing every time it changes.
  const isNearBottomRef = useRef(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    isNearBottomRef.current = true;
    setShowScrollToBottom(false);
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD;
    isNearBottomRef.current = nearBottom;
    setShowScrollToBottom(!nearBottom);
  }, []);

  // Auto-scroll only when the user was already at the latest — otherwise
  // surface the "scroll to latest" floater so streaming tokens don't
  // yank them away from a message they're reading.
  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollToBottom('auto');
    } else if (blocks.length > 0) {
      setShowScrollToBottom(true);
    }
  }, [blocks, pending, status, scrollToBottom]);

  useImperativeHandle(ref, () => ({
    /** Force the thread to the latest message regardless of where the
     * user was scrolled. Call after a send so the operator's new turn
     * is visible. */
    scrollToLatest: (behavior = 'smooth') => scrollToBottom(behavior),
  }), [scrollToBottom]);

  const showPlaceholder = (
    status === 'connected'
    && blocks.length === 0
    && !pending
    && placeholder
  );

  return (
    <>
      <div
        className={`chat-widget-messages${className ? ` ${className}` : ''}`}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {showPlaceholder && (
          <div className="chat-widget-placeholder">{placeholder}</div>
        )}
        {blocks.map((block) => (
          <ChatMessage
            key={block.id}
            block={block}
            onAnswer={onAnswer}
            onDecideApproval={onDecideApproval}
          />
        ))}
        {pending && (
          <div className="chat-widget-thinking">
            <i className="fa fa-spinner fa-spin" aria-hidden="true" />
            <span>Thinking…</span>
          </div>
        )}
        {error && <div className="chat-widget-error">{error}</div>}
      </div>

      {showScrollToBottom && (
        <button
          type="button"
          className="chat-widget-scroll-floater"
          onClick={() => scrollToBottom('smooth')}
          aria-label="Scroll to latest"
          title="Scroll to latest"
        >
          <i className="fa fa-arrow-down" aria-hidden="true" />
        </button>
      )}
    </>
  );
});

ChatThread.displayName = 'ChatThread';

export default ChatThread;
